import { useEffect, useState, useRef } from "react";
import { useTheme } from "next-themes";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useAuth, OAuthLinkRequiredError, MfaRequiredError } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { parseUrlParams, consumePostAuthRedirect, consumeStoredRedirect, isValidRedirectUrl } from "@/utils/oauth";
import { Loader2, CheckCircle, XCircle, AlertCircle, Eye, EyeOff, KeyRound, ChevronLeft } from "lucide-react";
import { OtpInput } from "@/components/ui/OtpInput";
import { setLastLoginProvider } from "@/utils/lastLoginProvider";

// Generic .. OAuth Callback Handler with Call Protection
export const OAuthCallback = () => {
  const { provider: urlProvider } = useParams<{ provider: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { loginWithOAuth, linkOAuthWithPassword, completeMfaChallenge } = useAuth();
  const { toast } = useToast();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  // Protection against multiple calls
  const hasProcessedRef = useRef(false);
  const processingRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const [status, setStatus] = useState<'loading' | 'success' | 'error' | 'link_required' | 'mfa_required'>('loading');
  const [message, setMessage] = useState('Processing authentication...');

  // MFA challenge state
  const [mfaToken, setMfaToken] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);
  const [mfaLoading, setMfaLoading] = useState(false);
  const [mfaError, setMfaError] = useState('');

  // Linking-flow state (only used when status === 'link_required')
  const [pendingLinkToken, setPendingLinkToken] = useState('');
  const [linkedEmail, setLinkedEmail] = useState('');
  const [linkPassword, setLinkPassword] = useState('');
  const [showLinkPassword, setShowLinkPassword] = useState(false);
  const [linkLoading, setLinkLoading] = useState(false);
  const [linkError, setLinkError] = useState('');

  // Extract provider from URL path if not available in params
  const getProviderFromPath = () => {
    if (urlProvider) return urlProvider;
    
    const pathSegments = location.pathname.split('/');
    const callbackIndex = pathSegments.findIndex(segment => segment === 'callback');
    
    if (callbackIndex !== -1 && pathSegments[callbackIndex + 1]) {
      return pathSegments[callbackIndex + 1];
    }
    
    return null;
  };
  
  const provider = getProviderFromPath();

  useEffect(() => {
    // Prevent multiple executions
    if (hasProcessedRef.current || processingRef.current) {
      return;
    }

    // Mark as processing immediately
    processingRef.current = true;

    // Create abort controller for this specific call
    abortControllerRef.current = new AbortController();

    const handleCallback = async () => {
      try {
        // Double-check we haven't processed this already
        if (hasProcessedRef.current) {
          return;
        }

        if (!provider) {
          throw new Error('Provider not specified in URL');
        }

        // Mark as processed early to prevent race conditions
        hasProcessedRef.current = true;

        // Parse parameters from URL
        const params = parseUrlParams(window.location.href);
        const { code, state, error, error_description } = params;

        // Check for OAuth errors first
        if (error) {
          throw new Error(error_description || error);
        }

        if (!code) {
          throw new Error('Authorization code not received from OAuth provider');
        }

        if (!state) {
          throw new Error('State parameter missing from OAuth callback');
        }

        setMessage(`Completing ${provider} authentication...`);

        // Check if we've been aborted before making the call
        if (abortControllerRef.current?.signal.aborted) {
          return;
        }

        // Complete OAuth login - this will call our backend
        await loginWithOAuth(provider, code, state);
        setLastLoginProvider(provider);

        // Check again if we've been aborted
        if (abortControllerRef.current?.signal.aborted) {
          return;
        }

        setStatus('success');
        setMessage('Authentication successful! Redirecting...');

        toast({
          title: "Welcome!",
          description: `Successfully signed in with ${provider.charAt(0).toUpperCase() + provider.slice(1)}`,
        });

        // Check for redirect parameter (from nginx auth_request or preview auth).
        // Order: explicit query param → sessionStorage (ProtectedRoute) →
        // localStorage saved by the `?rd=` flow (OAuthButtons / Login). The last
        // fallback is what carries preview share links through the SSO round-trip.
        const urlParams = new URLSearchParams(window.location.search);
        const redirectUrl = urlParams.get('rd') || urlParams.get('r');
        const queryRedirect = redirectUrl ? decodeURIComponent(redirectUrl) : null;

        const savedRedirect = consumePostAuthRedirect();
        const storedRedirect = consumeStoredRedirect();

        const candidate = queryRedirect || savedRedirect || storedRedirect;
        const redirectTo = candidate && isValidRedirectUrl(candidate) ? candidate : '/~';

        // Determine if this is an external URL that needs window.location.href
        // (any absolute URL starting with http:// or https://)
        const isExternalUrl = /^https?:\/\//.test(redirectTo);

        // Redirect after short delay
        setTimeout(() => {
          if (!abortControllerRef.current?.signal.aborted) {
            if (isExternalUrl) {
              // External redirect (container/preview URL) - use window.location
              window.location.href = redirectTo;
            } else {
              // Internal redirect - use navigate
              navigate(redirectTo, { replace: true });
            }
          }
        }, 1500);


      } catch (error) {
        // Only handle error if we haven't been aborted
        if (!abortControllerRef.current?.signal.aborted) {
          if (error instanceof MfaRequiredError) {
            setMfaToken(error.mfaToken);
            setStatus('mfa_required');
            return;
          }

          if (error instanceof OAuthLinkRequiredError) {
            // The email matches an existing password account.
            // Show the linking UI instead of an error screen.
            setPendingLinkToken(error.pendingLinkToken);
            setLinkedEmail(error.email);
            setStatus('link_required');
            // Don't redirect — stay on this page for the linking form.
            return;
          }

          setStatus('error');
          const errorMessage = error instanceof Error ? error.message : 'Authentication failed';
          setMessage(errorMessage);

          const isDeactivated = errorMessage.includes('deactivated');
          toast({
            variant: "destructive",
            title: isDeactivated ? "Account Deactivated" : "Authentication Failed",
            description: isDeactivated ? errorMessage : (errorMessage || 'Please try again'),
          });

          // Redirect to login after delay
          setTimeout(() => {
            if (!abortControllerRef.current?.signal.aborted) {
              navigate('/login', { replace: true });
            }
          }, 3000);
        }
      } finally {
        // Mark as no longer processing
        processingRef.current = false;
      }
    };

    // Add a small delay to ensure the page is fully loaded and prevent race conditions
    const timer = setTimeout(handleCallback, 200);
    
    // Cleanup function
    return () => {
      clearTimeout(timer);
      // Abort any ongoing requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      // Mark as no longer processing if we're cleaning up
      processingRef.current = false;
    };
  }, []); // Empty dependency array to run only once

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const getProviderDisplayName = (p?: string) => {
    if (!p) return 'OAuth Provider';
    return p.charAt(0).toUpperCase() + p.slice(1);
  };

  // Submit handler for the 2FA challenge
  const handleMfaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mfaCode.trim()) return;
    setMfaLoading(true);
    setMfaError('');
    try {
      await completeMfaChallenge(mfaToken, mfaCode.trim());
      setLastLoginProvider(provider);

      setStatus('success');
      setMessage('Authentication successful! Redirecting...');

      toast({
        title: "Welcome!",
        description: `Successfully signed in with ${getProviderDisplayName(provider)}`,
      });

      const savedRedirect = consumePostAuthRedirect();
      const storedRedirect = consumeStoredRedirect();
      const candidate = savedRedirect || storedRedirect;
      const redirectTo = candidate && isValidRedirectUrl(candidate) ? candidate : '/~';
      const isExternal = /^https?:\/\//.test(redirectTo);

      setTimeout(() => {
        if (isExternal) {
          window.location.href = redirectTo;
        } else {
          navigate(redirectTo, { replace: true });
        }
      }, 1000);
    } catch (err) {
      setMfaError(err instanceof Error ? err.message : 'Invalid code. Please try again.');
      setMfaCode('');
    } finally {
      setMfaLoading(false);
    }
  };

  // Submit handler for the password-verification / account-linking form
  const handleLinkAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!linkPassword) {
      setLinkError('Please enter your password.');
      return;
    }
    setLinkLoading(true);
    setLinkError('');
    try {
      await linkOAuthWithPassword(pendingLinkToken, linkPassword);
      setLastLoginProvider(provider);

      setStatus('success');
      setMessage(`${getProviderDisplayName(provider)} account connected successfully!`);

      toast({
        title: "Account Connected!",
        description: `Your ${getProviderDisplayName(provider)} account has been linked. You can now sign in with either method.`,
      });

      const linkCandidate = consumePostAuthRedirect() || consumeStoredRedirect();
      const redirectTo = linkCandidate && isValidRedirectUrl(linkCandidate) ? linkCandidate : '/~';

      setTimeout(() => {
        const isExternal = /^https?:\/\//.test(redirectTo);
        if (isExternal) {
          window.location.href = redirectTo;
        } else {
          navigate(redirectTo, { replace: true });
        }
      }, 1500);
    } catch (err) {
      setLinkError(
        err instanceof Error
          ? err.message
          : 'Failed to link account. Please check your password and try again.'
      );
    } finally {
      setLinkLoading(false);
    }
  };

  const getIcon = () => {
    switch (status) {
      case 'loading':
        return <Loader2 className="h-12 w-12 animate-spin text-[#9b87f5]" />;
      case 'success':
        return <CheckCircle className="h-12 w-12 text-green-500" />;
      case 'error':
        return <XCircle className="h-12 w-12 text-red-500" />;
      case 'link_required':
        return <AlertCircle className="h-12 w-12 text-amber-500" />;
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'loading':
        return 'text-[#9b87f5]';
      case 'success':
        return 'text-green-500';
      case 'error':
        return 'text-red-500';
      case 'link_required':
        return 'text-amber-600';
    }
  };

  // ── Two-factor authentication challenge ──
  if (status === 'mfa_required') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-warm-cream-100 via-white to-soft-gray-100 dark:from-background dark:via-background dark:to-background px-4">
        <div className="w-full max-w-sm">

          {/* Logo */}
          <div className="flex justify-center mb-8">
            <img src={isDark ? "/logo-dark.svg" : "/logo.svg"} alt="Joylo AI" className="h-12 w-auto" />
          </div>

          <div className="bg-white dark:bg-card rounded-3xl shadow-lg border border-gray-100 dark:border-white/10 overflow-hidden">
            <div className="h-1.5" style={{ background: "linear-gradient(135deg, hsl(4 86% 68%) 0%, hsl(262 83% 58%) 100%)" }} />

            <div className="px-8 py-8 text-center">
              {/* Icon */}
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-5"
                style={{ background: "linear-gradient(135deg, hsl(4 86% 68%) 0%, hsl(262 83% 58%) 100%)" }}>
                <KeyRound className="w-6 h-6 text-white" />
              </div>

              <h1 className="text-xl font-bold text-gray-900 dark:text-white mb-1">Two-Factor Authentication</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                {useRecoveryCode
                  ? "Enter one of your recovery codes"
                  : "Enter the 6-digit code from your authenticator app"}
              </p>

              <form onSubmit={handleMfaSubmit} className="space-y-5">
                {useRecoveryCode ? (
                  <input
                    value={mfaCode}
                    onChange={e => setMfaCode(e.target.value)}
                    placeholder="XXXXXX-XXXXXX-XXXXXX"
                    maxLength={26}
                    autoFocus
                    autoComplete="off"
                    className="w-full h-11 px-4 text-center border border-gray-200 dark:border-white/15 rounded-xl focus:outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-400/20 text-gray-900 dark:text-white bg-white dark:bg-white/5 dark:placeholder:text-gray-500"
                  />
                ) : (
                  <OtpInput value={mfaCode} onChange={setMfaCode} autoFocus />
                )}

                {mfaError && (
                  <p className="text-sm text-red-500 dark:text-red-400">{mfaError}</p>
                )}

                <button
                  type="submit"
                  disabled={mfaLoading || (useRecoveryCode ? !mfaCode.trim() : mfaCode.length < 6)}
                  className="w-full h-11 rounded-xl text-white font-semibold text-sm flex items-center justify-center gap-2 hover:opacity-90 transition-opacity shadow-md disabled:opacity-50"
                  style={{ background: "linear-gradient(135deg, hsl(4 86% 68%) 0%, hsl(262 83% 58%) 100%)" }}
                >
                  {mfaLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                  {mfaLoading ? "Verifying…" : "Verify"}
                </button>
              </form>

              <div className="mt-5 flex flex-col items-center gap-3 text-sm">
                <button
                  type="button"
                  className="text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 font-medium transition-colors"
                  onClick={() => { setUseRecoveryCode(r => !r); setMfaCode(''); setMfaError(''); }}
                >
                  {useRecoveryCode ? "Use authenticator app instead" : "Use a recovery code instead"}
                </button>
                <button
                  type="button"
                  className="flex items-center gap-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors text-xs"
                  onClick={() => navigate('/login', { replace: true })}
                >
                  <ChevronLeft className="w-3.5 h-3.5" /> Back to login
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Linking form (shown when the email already belongs to a password account) ──
  if (status === 'link_required') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="max-w-md mx-auto text-center p-8">
          <div className="mb-6 flex justify-center">
            <AlertCircle className="h-12 w-12 text-amber-500" />
          </div>

          <h1 className="text-2xl text-black dark:text-white font-bold mb-2">
            Verify your identity
          </h1>

          <p className="text-sm text-muted-foreground mb-6">
            An account with <span className="font-medium text-foreground">{linkedEmail}</span> already
            exists. Enter your password to connect your{' '}
            <span className="font-medium text-foreground">{getProviderDisplayName(provider)}</span> account.
          </p>

          <form onSubmit={handleLinkAccount} className="space-y-4 text-left">
            <div className="relative">
              <input
                type={showLinkPassword ? 'text' : 'password'}
                value={linkPassword}
                onChange={e => { setLinkPassword(e.target.value); setLinkError(''); }}
                placeholder="Your existing password"
                autoFocus
                className="w-full px-4 py-3 pr-12 border border-input rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-[#9b87f5]"
              />
              <button
                type="button"
                onClick={() => setShowLinkPassword(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                tabIndex={-1}
                aria-label={showLinkPassword ? 'Hide password' : 'Show password'}
              >
                {showLinkPassword
                  ? <EyeOff className="h-5 w-5" />
                  : <Eye className="h-5 w-5" />}
              </button>
            </div>

            {linkError && (
              <p className="text-sm text-red-500 text-center">{linkError}</p>
            )}

            <button
              type="submit"
              disabled={linkLoading || !linkPassword}
              className="w-full py-3 px-4 bg-[#9b87f5] hover:bg-[#8B5CF6] disabled:opacity-50 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              {linkLoading && <Loader2 className="h-4 w-4 animate-spin" />}
              {linkLoading ? 'Connecting…' : `Connect ${getProviderDisplayName(provider)} account`}
            </button>
          </form>

          <div className="mt-6 pt-4 border-t border-border">
            <p className="text-xs text-muted-foreground mb-2">
              Don't want to link accounts?
            </p>
            <button
              onClick={() => navigate('/login', { replace: true })}
              className="text-sm text-[#9b87f5] hover:text-[#8B5CF6] underline"
            >
              Sign in with your password instead
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Default status screens (loading / success / error) ──
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="max-w-md mx-auto text-center p-8">
        <div className="flex justify-center mb-8">
          <img src={isDark ? "/logo-dark.svg" : "/logo.svg"} alt="Joylo AI" className="h-12 w-auto" />
        </div>
        <div className="mb-6 flex justify-center">
          {getIcon()}
        </div>

        <h1 className="text-2xl text-black dark:text-white font-bold mb-4">
          {status === 'loading' && `Authenticating with ${getProviderDisplayName(provider)}...`}
          {status === 'success' && 'Success!'}
          {status === 'error' && 'Authentication Failed'}
        </h1>

        <p className={`text-lg mb-6 ${getStatusColor()}`}>
          {message}
        </p>

        {status === 'loading' && (
          <div className="space-y-2">
            <div className="h-2 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-[#9b87f5] to-[#8B5CF6] rounded-full animate-pulse"
                style={{ width: '70%' }}
              />
            </div>
            <p className="text-sm text-muted-foreground">
              Verifying your identity and setting up your account...
            </p>
          </div>
        )}

        {status === 'error' && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              You will be redirected to the login page shortly.
            </p>
            <button
              onClick={() => navigate('/login', { replace: true })}
              className="text-[#9b87f5] hover:text-[#8B5CF6] underline"
            >
              Go to login now
            </button>
          </div>
        )}

        {status === 'success' && (
          <p className="text-sm text-muted-foreground">
            Taking you to your dashboard...
          </p>
        )}
      </div>
    </div>
  );
};

// Specific provider callbacks with proper routing
export const GoogleCallback = () => <OAuthCallback />;
export const GitHubCallback = () => <OAuthCallback />;
export const FacebookCallback = () => <OAuthCallback />;
export const AppleCallback = () => <OAuthCallback />;
export const MicrosoftCallback = () => <OAuthCallback />;